/* eslint-disable arrow-body-style */
import { cloneDeep } from 'lodash-es';
import http from '@/utils/http';
import { importJSON } from '@intelligence/utils/echartsUtils';
import { formatRegionOptionsByCond } from '@intelligence/v3/utils/region';
import { IntelligenceRequestAt } from '@intelligence/enums/common';

import type { AxiosRequestConfig } from 'axios';
import type { IRegion } from '@home/types/common';
import type { IBaseResponse, INewMarkCoreData, GeoJSON, IShortIdUrlParams } from '@intelligence/types/common';
import type { IDBIMenuDTO } from '@intelligence/types/menu';
import type { INewMarkAccessRecord } from '@intelligence/types/newMark';

export const fetchConfigureTexts = (payload?: {
  prefix?: string;
}): IBaseResponse<{ configure_texts: Record<string, string> }> => {
  return getQueryDefinition({
    configure_texts: {
      target: 'game_config',
      params: {
        key: payload?.prefix || '*',
      },
    },
  });
};

/** 获取广告位信息（base_url 不传则返回所有页面的广告位） */
export const fetchAd = (params: { base_url?: string; type?: string }): Promise<any> => {
  return http({
    url: '/api/v1/intelligence_pc/ad',
    method: 'post',
    data: params,
    rawData: true,
  }) as Promise<any>;
};

export const addSeachRecord: (payload: any) => Promise<any> = (payload) => {
  return http({
    url: '/api/v1/intelligence_pc/addSeachRecord',
    method: 'post',
    data: payload,
    rawData: true,
  }) as Promise<any>;
};

export const delSeachRecord: (payload: any) => Promise<any> = (payload) => {
  return http({
    url: '/api/v1/intelligence_pc/delSeachRecord',
    method: 'post',
    data: payload,
    rawData: true,
  }) as Promise<any>;
};

export const seachRecordList: (payload: any) => Promise<any> = (payload) => {
  return http({
    url: '/api/v1/intelligence_pc/seachRecordList',
    method: 'post',
    data: payload,
    rawData: true,
  }) as Promise<any>;
};

export const seachRecordListPlus: (payload: any) => Promise<any> = (payload) => {
  return http({
    url: '/api/v1/intelligence_pc/search_record_list',
    method: 'post',
    data: payload,
    rawData: true,
  }) as Promise<any>;
};

export const getFilterSaveList: (payload: any) => Promise<any> = (payload) => {
  return http({
    url: '/api/v1/intelligence_pc/userFilterList',
    method: 'post',
    data: payload,
    rawData: true,
  }) as Promise<any>;
};

export const updateFilterSaveList: (payload: any) => Promise<any> = (payload) => {
  return http({
    url: '/api/v1/intelligence_pc/updataUserFilterList',
    method: 'post',
    data: payload,
    rawData: true,
  }) as Promise<any>;
};

export const delFilterSaveList: (payload: any) => Promise<any> = (payload) => {
  return http({
    url: '/api/v1/intelligence_pc/delUserFilterList',
    method: 'post',
    data: payload,
    rawData: true,
    errorHandler: true,
  }) as Promise<any>;
};

export const getTicketDatas = (params: any): Promise<any> => {
  return http({
    url: '/api/v1/intelligence_pc/getTicket',
    method: 'post',
    data: params,
    rawData: true,
  }) as Promise<any>;
};

// 支持的报告语种
const supportLangs = ['en', 'cn'];
// 支持分语种获取的报告类型
const supportLangSystemIds = ['intelligence_ai', 'intelligence_insights', 'gameResearch'];
export const fetchPDFFileStream = (
  data: { systemId: string; resourceId: string; lang: string | undefined },
  params?: Record<string, string>,
): Promise<any> => {
  const { systemId, resourceId, lang = 'en' } = data || {};

  let url = `/api/v1/util/resource/${systemId}/${resourceId}`;
  if (supportLangSystemIds.includes(systemId) && supportLangs.includes(lang)) {
    url = `${url}/${lang}`;
  }

  return http({
    url,
    method: 'get',
    params: { ...params, download: true },
    data: {},
    responseType: 'arraybuffer',
    rawData: true,
    original: true,
    errorHandler: true,
  });
};

export const fetchReportExcel = (data: { id: string; name: string }): Promise<any> => {
  return http({
    url: `/api/v1/util/report/attachment/download`,
    method: 'post',
    data,
    responseType: 'arraybuffer',
    rawData: true,
    download: true,
  });
};

export const fetchPDFFileStreamSearch = (
  data: {
    systemId: string;
    resourceId: string;
    lang: string | undefined;
  },
  params?: Record<string, string>,
): Promise<any> => {
  const { systemId, resourceId, lang } = data || {};
  return http({
    url: `/api/v1/util/resource/${systemId}/${resourceId}/${lang}`,
    method: 'get',
    params: { ...params, download: true },
    data: {},
    responseType: 'arraybuffer',
    rawData: true,
    original: true,
    errorHandler: true,
  });
};

export const fetchLeftMenus = (): IBaseResponse<IDBIMenuDTO[]> => {
  return http({
    url: '/api/v1/intelligence_pc/listMenuSnapshot',
    method: 'post',
    data: {},
    rawData: true,
  }) as Promise<any>;
};

export const getQueryDefinition: (payload?: any, cache?: boolean) => Promise<any> = (payload, cache = true) => {
  const url = cache ? '/api/v1/intelligence_pc/cond' : '/api/v1/intelligence_pc/cond?interfaceCache=false';
  return http({
    url,
    method: 'post',
    data: payload,
    rawData: true,
  }) as Promise<any>;
};

export const getHotTagsList = (entity_type: 'mobile' | 'combine'): Promise<any> => {
  return http({
    url: '/api/v1/intelligence_pc/hotTagsList',
    method: 'post',
    data: { entity_type },
    rawData: true,
  }) as Promise<any>;
};

export const fetchNewMarkAccessRecords = (): IBaseResponse<Record<string, INewMarkAccessRecord>> => {
  return http({
    url: '/api/v1/intelligence_pc/user/get_marks',
    method: 'post',
    data: {},
    rawData: true,
  }) as IBaseResponse<any>;
};

export const addNewMarkRecord = (params: INewMarkCoreData): IBaseResponse<any> => {
  return http({
    url: '/api/v1/intelligence_pc/addNewMarkRecord',
    method: 'post',
    data: params,
    rawData: true,
  }) as IBaseResponse<any>;
};

export const writeShortUrlParams = (params: { system_type: string; params: string }): any => {
  return http({
    url: '/api/v1/common/writeShortUrlParams',
    method: 'post',
    data: params,
    rawData: true,
  });
};

export const getShortUrlParams = (params: { key: string }): any => {
  return http({
    url: '/api/v1/common/getShortUrlParams',
    method: 'post',
    data: params,
    rawData: true,
  });
};

/**
 * 获取短连接的具体参数
 * @param params 参数对象
 * @param params.key 短连接id
 * @param params.mode 模式，暂时固定为query
 * @return 短连接对应的具体参数
 */
export const getShortIdUrlParams = (params: {
  key: string;
  mode?: 'query' | (string & {});
}): IBaseResponse<IShortIdUrlParams> => {
  return http({
    url: '/api/v1/intelligence_pc/get_short_url',
    method: 'post',
    data: { mode: 'query', ...params },
    rawData: true,
  }) as any;
};

// 通用，获取游戏信息接口
export const getGameInfoById: (payload: IGameDetailsFetchParams) => Promise<any> = (payload) => {
  return http({
    url: '/api/v1/intelligence_pc/entity/detail',
    method: 'post',
    data: payload,
    rawData: true,
    errorHandler: true,
  }) as Promise<any>;
};

// 通用，获取游戏信息接口
export const getGameInfoByIdPlus: (payload: IGameDetailsFetchParams, config?: AxiosRequestConfig) => Promise<any> = (payload, config) => {
  return http({
    ...config,
    url: '/api/v1/intelligence_pc/entity/detail_plus',
    method: 'post',
    params: { ...config?.params, _at: IntelligenceRequestAt.Info },
    data: payload,
    rawData: true,
    errorHandler: true,
  }) as Promise<any>;
};

// 世界地图数据（缓存）
let geoJSONCached: GeoJSON | null = null;

/**
 * 获取世界地图数据
 */
export const fetchGeoJSON = async (): Promise<GeoJSON> => {
  // 有缓存直接返回缓存世界地图数据的拷贝
  if (geoJSONCached !== null) return cloneDeep(geoJSONCached);
  // 没有缓存则从服务端拉取
  const geoJSON = await importJSON('https://ogdb-cdn.intlgame.cn/front/static/world.json');
  if (!!geoJSON) {
    // 设置缓存
    geoJSONCached = geoJSON;
  }
  return geoJSON;
};

export const getQueryDefinitionX: (payload?: any) => Promise<any> = (payload) => {
  return http({
    url: '/api/v1/intelligence_pc/condx',
    method: 'post',
    data: payload,
    rawData: true,
  }) as Promise<any>;
};

interface IFetchOptionParams {
  action: 'get';
  keys: string[];
}

interface IUpdateOptionParams {
  action: 'set';
  key: string;
  value: string;
}

export const fetchOrUpdateOption = (
  params: IFetchOptionParams | IUpdateOptionParams,
): IBaseResponse<IRecord<string>> => {
  return http({
    method: 'post',
    url: '/api/v1/intelligence_pc/user/option',
    data: params,
    rawData: true,
  }) as any;
};

export const fetchGameGenreProfileMetricsNoType: (payload: any) => Promise<any> = (payload) => {
  return http({
    url: '/api/v1/intelligence_pc/game/genre_profile/metrics',
    method: 'post',
    data: payload,
    rawData: true,
  }) as Promise<any>;
};

/**
 * 获取 intelligence 全量地区国家数据
 * @returns Promise<IRegion[]> 地区国家数据
 */
export const fetchRegionCountryData = async (): Promise<IRegion[]> => {
  try {
    const response: any = await http({
      url: '/api/v1/intelligence_pc/cond',
      method: 'post',
      rawData: true,
      data: {
        // 随便加一个，避免接口返回全量配置，浪费带宽资源
        filterTags: {
          target: 'filter_tags',
        },
        use_market_info: [
          'intel_region -> market',
          'intel_region -> en',
          'intel_region -> cn',
          'market -> en',
          'market -> cn',
        ],
      },
    });

    // 从接口返回值中提取地区数据
    const marketInfo = response?.ext?.market_info || {};

    // 转换为 IRegion[] 格式
    return formatRegionOptionsByCond(marketInfo) as IRegion[];
  } catch (error) {
    console.error('获取地区国家数据失败:', error);
    return [];
  }
};
